import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Subscription } from 'rxjs';
import { AlertsSacModalComponent } from '../components/price-alerts/alerts-sac-modal.component';
import { CookieService } from '@shopping-angular-components/packages-commons';
import { UtilsService } from './utils.service';
import { LandingModelService } from './landingModel.service';
import { Currency } from '../model/landingModel';
import { isPlatformBrowser } from '@angular/common';
import { AlertBannerModel } from '../components/price-alerts/alerts-banner.component';
import { AlertsService } from '@shopping-angular-components/alerts';
import { ScreenType } from '@shopping-angular-components/common-library';
import { FiltersService } from 'projects/common/services/filters.service';


declare global {
  interface Window {
    upaData?: any;
    locale: string;
  }
}

@Injectable({ providedIn: 'root' })
export class PriceAlertsService {
    private static PRICE_ALERT_BANNER_CLICKED_LS_KEY = 'price-alert-banner-clicked';
    public isEnabled: boolean = false;
    public alertCalled: boolean = false;

    public alertCreated: boolean = false;
    private currency: Currency;
    isWebView: boolean;

    flightsOptions: any;
    isLogged: boolean;
    isMobile: boolean;
    domain: string;
    locale: string;
    trackerId: string;
    socialId: string;
    private readonly isBrowser: boolean;


    constructor(private landingModelService: LandingModelService,
                private alertsService: AlertsService,
                private cookieService: CookieService,
                private utilsService: UtilsService,
                private filtersService: FiltersService,
                @Inject(PLATFORM_ID) private platformId: object
                ) {

        this.isBrowser = isPlatformBrowser(this.platformId);

        this.currency = this.landingModelService.getLandingModel().currencies.primaryCurrency;
        this.isWebView = landingModelService.getLandingModel().webview;
        this.isMobile =  this.landingModelService.getLandingModel().device.type === ScreenType.MOBILE;

            if (this.isBrowser) {
            // @ts-ignore
            document.addEventListener('login.complete', (event: CustomEvent) => {
                if (event?.detail && event?.detail?.dss2){
                    let hrefActual: string = window.location.href.replace(window.origin, '');
                    let hrefActualWithoutOrigin: string = hrefActual + (hrefActual.includes('?') ? "&" : "?") + 'refererEvent=priceAlert';

                    window.location.href = hrefActualWithoutOrigin;
                } else {
                    console.error('Error in login.complete event');
                }
            });
        }
    }

    setPriceAlertsData(data: AlertBannerModel): void {
        if (!this.isBrowser) {
            this.isEnabled = false;
            return;
        }

      const upaData = window.upaData;
      this.domain = upaData?.dn;
      this.trackerId = upaData?.userid;
      this.socialId = upaData?.despegarid;

      this.isLogged = this.utilsService.isLogged();

      this.flightsOptions = {
        locale: window.locale,
        language: upaData?.lan,
        socialId: this.socialId,
        partnerId: upaData?.partner_id,
        companyId: upaData?.company_id,
        countryCode: upaData?.cc,
        cityFromCode: data.origin_code,
        cityFromGid: data.origin_gid,
        cityToCode: data.destination_code,
        cityToGid: data.destination_gid,
        destinationCityDescription: data.destination_city_description,
        searchType: data.flight_type,
        lowestPrice: {
          "currencyCode": data.price_currency_code,
          "amount": data.price_amount,
          "formattedPrice": data.formatted_price
        },
      };

      this.isEnabled = true;
    }

    private redirectToLogin(): void {
        if (this.isNativeLogin()) { // TODO check, existe?
            const event = new CustomEvent('login');
            globalThis.document.dispatchEvent(event);
        } else {
            let hrefActual: string = window.location.href.replace(window.origin, '');
            let hrefActualWithoutOrigin: string = hrefActual + (hrefActual.includes('?') ? "&" : "?") + 'refererEvent=priceAlert';
            let redirectHref: string = '/login?redirect_to=' + encodeURIComponent(hrefActualWithoutOrigin);
            window.location.href = redirectHref;
        }
    }

    private isNativeLogin(): boolean {
        let xDespAppFeatures = this.cookieService.get('xdesp-AppFeatures');
        if (this.isWebView && xDespAppFeatures) {
            return xDespAppFeatures.includes('login');
        }

        return false;
    }

    private openAlertsWebView(): void{
        if (!this.isBrowser) {
            return;
        }

        if (!this.isLogged && !window?.location?.href.includes('refererEvent=priceAlert')){
            this.redirectToLogin();
        } else if (this.isLogged) {
            this.alertCreated = true;
            this.callAlert('');
        }
    }

    private openAlertsNotWebView(): void {
        if (!this.isBrowser) {
            return;
        }

        if (!this.alertCreated) {

            const loginAlertsService = this.alertsService.Login({
                country: this.flightsOptions.countryCode,
                language: this.flightsOptions.language.toLowerCase(),
                domain: window.location.protocol + '//' + window.location.hostname
            });

            loginAlertsService.getLocalUserStatus((localUserError, localUserStatus) => {
                if (!localUserError || localUserStatus === 'NOT_LOGGED') {
                    loginAlertsService.getUserInfo((userInfoError, userInfoStatus) => {
                        if (userInfoStatus?.email) {
                            this.callAlert(userInfoStatus.email);
                            this.alertCreated = true;
                        }else {
                            if (window?.location?.href.indexOf('refererEvent=priceAlert') < 0) {
                                this.redirectToLogin();
                            }
                        }
                    });
                }
            });
        } else {
            AlertsSacModalComponent.openAlertsModal.emit("error");
        }
    }

    openAlerts(): void {
        if (!this.isBrowser) {
            return;
        }

        if (this.isWebView) {
            this.openAlertsWebView();
        } else {
            this.openAlertsNotWebView();
        }
    }

    private callAlert(email?:string): void {
        if (this.isWebView) {
            this.alertCreated = true;
        }
        this.alertCalled = true;

        const cityName = this.flightsOptions.destinationCityDescription;;
        const price = this.flightsOptions.lowestPrice;
        const months = this.getSearchedMonthsOrNext7();

        const stay = {
            months: months,
            type: 'rangedStay',
            on_weekends: true,
            min_stay: '1',
            max_stay: '10'
        };

        let options = {
            type: 'flight',
            one_way: (this.flightsOptions.searchType === 'ONE_WAY' || this.flightsOptions.searchType === 'ONEWAY')
        };

        if (price) {
            options['price'] = {
                amount: price.amount.toString(),
                currency_code: price.currencyCode
            }
        }
        let cookie = this.utilsService.getCookieValue("AppAllowsNotifications")

        AlertsSacModalComponent.openAlertsModal.emit({
            locale: this.flightsOptions.locale,
            cityName: cityName,
            mail: email,
            webView: this.isWebView,
            toastMode: this.isMobile,
            createWithoutEmail: this.isWebView && email === '',
            isWl: !!this.flightsOptions.partnerId,
            showToastActivateNotification: (this.isWebView && (!cookie || cookie === 'false')),
            data: {
                client: {
                    email: email
                },
                alert_type: 'USER_CREATED',
                social_id: this.flightsOptions.socialId,
                company_id: this.flightsOptions.companyId,
                price: price.formattedPrice,
                country: this.flightsOptions.countryCode,
                language: this.flightsOptions.language.toLowerCase(),
                route: {
                    origin: this.flightsOptions.cityFromCode,
                    origin_gid: this.flightsOptions.cityFromGid,
                    destination: this.flightsOptions.cityToCode,
                    destination_gid: this.flightsOptions.cityToGid
                },
                options: [
                    options
                ],
                stay: stay
            },
            flow: 'LANDING',
            createWithoutModal: false
        });
        this.alertCreated = true;
    }

    private formatMonthYear(date: Date): {"month": string, "year": string} {
        return {
            month: date.toLocaleString('en-US', { month: 'long' }).toUpperCase(),
            year: date.getFullYear().toString()
        };
    }

    private parseMonthString(monthStr: string): {"month": string, "year": string} {
        const year = monthStr.substring(0, 4);
        const monthNum = monthStr.substring(4, 6);
        const date = new Date(Number(year), parseInt(monthNum) - 1, 1);
        return this.formatMonthYear(date);
    }

    private getNextMonths(count: number): {"month": string, "year": string}[] {
        const currentDate = new Date();
        const months: {"month": string, "year": string}[] = [];
        for (let i = 0; i < count; i++) {
            const monthDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
            months.push(this.formatMonthYear(monthDate));
        }
        return months;
    }

    private getSearchedMonthsOrNext7(): {"month": string, "year": string}[] {
        const urlMonths = this.isBrowser
            ? new URLSearchParams(window.location.search).get('outboundMonthRanges')
            : null;
        if (urlMonths) {
            return urlMonths.split(',').map(month => this.parseMonthString(month));
        }

        const filteredMonths = this.filtersService.getFilteredFullMonths();
        if (filteredMonths?.length > 0) {
            return filteredMonths.map(month => this.parseMonthString(month));
        }

        return this.getNextMonths(7);
    }

    saveClickFlagOnLocalStorage(): void {
        if (!this.isBrowser) {
            return;
        }

        localStorage.setItem(PriceAlertsService.PRICE_ALERT_BANNER_CLICKED_LS_KEY, this.getLocalStorageKey());
    }

    removeClickFlagFromLocalStorage(): void {
        if (!this.isBrowser) {
            return;
        }

        localStorage.removeItem(PriceAlertsService.PRICE_ALERT_BANNER_CLICKED_LS_KEY);
    }

    existsClickFlagInLocalStorage(): boolean {
        if (!this.isBrowser) {
            return false;
        }

        return localStorage.getItem(PriceAlertsService.PRICE_ALERT_BANNER_CLICKED_LS_KEY) === this.getLocalStorageKey();
    }

    getLocalStorageKey(): string {
        return `${this.flightsOptions.cityFromGid}-${this.flightsOptions.cityToGid}-${this.flightsOptions.lowestPrice.formattedPrice}-${this.flightsOptions.countryCode}`;
    }
}
